import {
  useEffect,
  useMemo,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  FamilyMemberForm,
  FamilyMemberFormRef,
} from '../components/FamilyMemberForm';
import {
  findDependent,
  mapDependentsModel,
} from 'theSource/features/FamilyMembers/utils';
import { FirstDrawerDividerMargin } from '../FamilyDetailsPageStyled';
import { InternalPage } from 'theSource/components';
import { FamilyDrawerDisplayPicture } from '../FamilyDrawerDisplayPicture';
import { AvatarFamilyDrawerDisplayPicture } from '../AvatarFamilyDrawerDisplayPicture';
import { setDependentDisplay } from 'enrollment/features/ProfileEdit/Avatar/AvatarActions';
import { ProfileCropper } from '../ProfileCropper';
import { getInitialValues, queryClient, useApiMutation } from 'theSource/utils';
import { ErrorResponseDto, FormValuesDto, ResponseDto } from 'theSource/types';
import {
  EDIT_DEPENDENT_QUERY_KEY,
  GET_DEPENDENTS_QUERY_KEY,
} from 'enrollment/queries/useEnrollmentFamilyQuery';
import { useConfigurationQuery } from 'theSource/queries';
import {
  hairColorPalette,
  shirtColorPalette,
  skinColorPalette,
} from 'enrollment/app/constants';
import { validateFields } from 'enrollment/utils/FieldUtils/fieldUtils';
import { PsButtonTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { LoaderOverlay } from 'enrollment/components';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';

export interface EditFamilyMemberProps {
  enrollmentContextType?: string | null;
  data: any;
  dependents: any;
  dependentId: any;
  updateDependentsData?: any;
  openFamilyDetailsDrawer: boolean;
  setOpenAddFamilyDetailsDrawer: Dispatch<SetStateAction<boolean>>;
}

export const EditFamilyMemberDrawer = ({
  enrollmentContextType,
  data,
  dependents,
  dependentId,
  // eslint-disable-next-line
  updateDependentsData,
  openFamilyDetailsDrawer,
  setOpenAddFamilyDetailsDrawer,
}: EditFamilyMemberProps) => {
  const { t } = useTranslation('pages.enrollmentFamily');

  const [openFamilyAvatarDrawer, setOpenFamilyAvatarDrawer] = useState(false);
  const [saveAvatarName, setSaveAvatarName] = useState<string>('');
  const [, setSaveDisplayPicture] = useState<string>('');
  const [saveAvatarSkinTone, setSaveAvatarSkinTone] = useState<string>('');
  const [saveAvatarHairColor, setSaveAvatarHairColor] = useState<string>('');
  const [saveAvatarShirtColor, setSaveAvatarShirtColor] = useState<string>('');
  const [orientationDivider, setOrientationDivider] = useState<
    'horizontal' | 'vertical'
  >('horizontal');
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [familyDisplayPictureSaved, setFamilyDisplayPictureSaved] =
    useState<boolean>(false);
  // eslint-disable-next-line
  const [awaitSuccess, setAwaitSuccess] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const familyMemberDrawerId = 'edit-family-member-drawer';

  const toast = useToastDialog();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setDialogRef = useCallback((el: HTMLDivElement) => {
    containerRef.current = el;
  }, []);

  const familyContainerWidth = document.querySelector(
    '#enrollment-family-container'
  ) as HTMLElement;

  useEffect(() => {
    familyContainerWidth?.offsetWidth <= 945
      ? setOrientationDivider('horizontal')
      : setOrientationDivider('vertical');
  }, [familyContainerWidth]);

  const dispatch = useDispatch();

  const addDrawerFamilyAvatar = useSelector((state: any) => state?.enrollment);
  const avatarObject = addDrawerFamilyAvatar?.chooseAvatar;
  const avatarName = avatarObject?.family_avatar?.name ?? '';
  const avatarSkinTone = avatarObject?.avatarFamilySkinTone?.name ?? '';
  const avatarHairColor = avatarObject?.avatarFamilyHairColor?.name ?? '';
  const avatarShirtColor = avatarObject?.avatarFamilyShirtColor?.name ?? '';
  const displayPicture = avatarObject?.croppedFamilyImagePath ?? '';
  const chosenFamilyDisplay = avatarObject?.chosenFamilyDisplay ?? '';
  const isFamilyCropped = avatarObject?.isFamilyCropped ?? '';

  const subscriberProfileAvatarValue: string[] = [];
  avatarName && subscriberProfileAvatarValue.push(avatarName);
  avatarSkinTone && subscriberProfileAvatarValue.push(avatarSkinTone);
  avatarHairColor && subscriberProfileAvatarValue.push(avatarHairColor);
  avatarShirtColor && subscriberProfileAvatarValue.push(avatarShirtColor);

  const handleCloseAvatarDrawer = () => {
    setOpenFamilyAvatarDrawer(false);
  };

  const handleUploadDrawerAvatar = () => {
    setSaveAvatarName(avatarName);
    setSaveAvatarSkinTone(avatarObject?.avatarFamilySkinTone?.colorCode);
    setSaveAvatarHairColor(avatarObject?.avatarFamilyHairColor?.colorCode);
    setSaveAvatarShirtColor(avatarObject?.avatarFamilyShirtColor?.colorCode);
    setOpenFamilyAvatarDrawer(false);
    dispatch(
      setDependentDisplay({
        profilePicture: '',
        dependentDisplay: 'avatar',
        isFamilyCropped: '',
      })
    );
    setFamilyDisplayPictureSaved(false);
    setSaveDisplayPicture('');
  };

  const dependentInfo = useMemo(
    () => findDependent(dependents, dependentId!.toString()),
    [dependents, dependentId]
  );

  const categories = useMemo(
    () => dependentInfo && mapDependentsModel(data, dependentInfo),
    [data, dependentInfo]
  );

  const initialValues = useMemo(
    () => getInitialValues(categories),
    [categories]
  );

  // eslint-disable-next-line
  const [updatedValues, setUpdatedValues] = useState(initialValues);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const removeListenerController = new AbortController();

  const handleCloseFamilyDrawer = () => {
    setOpenAddFamilyDetailsDrawer(false);
    if (
      !familyDisplayPictureSaved &&
      (subscriberProfileAvatarValue.length === 0 ||
        dependentInfo?.dependent_profile?.avatar ===
          subscriberProfileAvatarValue?.join('_'))
    ) {
      return;
    }
    queryClient.invalidateQueries(
      `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`
    );
  };

  const handleSaveFamilyPictureDrawer = () => {
    setSaveDisplayPicture(displayPicture);
    setFamilyDisplayPictureSaved(true);
  };

  const { mutate: editFamilyMember } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    FormValuesDto
  >({
    queryKey: EDIT_DEPENDENT_QUERY_KEY,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/dependents/profile/${dependentId}/${enrollmentContextType}`,
    onSuccess: () => {
      setAwaitSuccess?.(false);

      document.addEventListener('psSubmitDialog', (e) => {
        setIsSubmitting(false);
        if ((e as CustomEvent).detail === familyMemberDrawerId) {
          setAwaitSuccess?.(true);
          setOpenAddFamilyDetailsDrawer(false);

          queryClient.invalidateQueries(
            `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`
          );
        }
      });
    },
    onError: (error) => {
      setIsSubmitting(false);
      if (containerRef.current) {
        toast.showToast({
          message:
            error.response?.data?.details ||
            error.message ||
            'An unknown error occurred',
          status: 'danger',
          closeIcon: true,
          container: containerRef.current,
        });
      }
    },
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
  });

  const formRef = useRef<FamilyMemberFormRef>(null);

  const handleSubmit = () => {
    if (formRef.current) {
      const submissionObject = formRef.current.getSubmissionObject();
      setSaveDisabled(true);
      setIsSubmitting(true);
      editFamilyMember(submissionObject);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    removeListenerController.abort();
    switch (e.target?.type) {
      case 'checkbox':
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: e.target?.checked,
        }));
        break;
      default:
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: value,
        }));
        break;
    }
    validateFields(e.target);
  };

  useEffect(() => {
    const avatar = dependentInfo?.dependent_profile?.avatar;
    const profilePicture = dependentInfo?.dependent_profile?.profile_picture;
    const avatarName = avatar?.split('_');
    const avatarSkincolor = skinColorPalette.filter(
      (item: any) => avatarName && avatarName[1] === item.name
    );
    const avatarHairColor = hairColorPalette.filter(
      (item: any) => avatarName && avatarName[2] === item.name
    );
    const avatarShirtColor = shirtColorPalette.filter(
      (item: any) => avatarName && avatarName[3] === item.name
    );

    const newAvatarName = avatarName === undefined ? '' : avatarName[0];
    const newAvatarSkinTone = avatarSkincolor[0]?.colorCode;
    const newAvatarHairColor = avatarHairColor[0]?.colorCode;
    const newAvatarShirtColor = avatarShirtColor[0]?.colorCode;
    const newDisplayPicture = profilePicture || '';
    const dependentDisplay = profilePicture
      ? 'picture'
      : avatar
        ? 'avatar'
        : '';
    const isFamilyCropped = profilePicture ? true : '';
    profilePicture && setFamilyDisplayPictureSaved(true);

    dispatch(
      setDependentDisplay({
        profilePicture: profilePicture,
        dependentDisplay: dependentDisplay,
        isFamilyCropped: isFamilyCropped,
      })
    );
    setSaveAvatarName(newAvatarName);
    setSaveAvatarSkinTone(newAvatarSkinTone);
    setSaveAvatarHairColor(newAvatarHairColor);
    setSaveAvatarShirtColor(newAvatarShirtColor);
    setSaveDisplayPicture(newDisplayPicture);
    // eslint-disable-next-line
  }, [dependentInfo]);

  const renderAvatarButton = () => {
    return (chosenFamilyDisplay === 'picture' && isFamilyCropped) ||
      !chosenFamilyDisplay ||
      (chosenFamilyDisplay === 'avatar' && !saveAvatarName) ? (
      <PsButtonTuner
        buttonId="select-avatar-btn"
        label={t('selectAvatarButtonLabel')}
        level="tertiary"
        onClick={() => {
          setOpenFamilyAvatarDrawer(true);
        }}
      />
    ) : chosenFamilyDisplay === 'avatar' && saveAvatarName ? (
      <PsButtonTuner
        buttonId="edit-avatar-btn"
        label={t('editAvatarButtonLabel')}
        level="tertiary"
        onClick={() => {
          setOpenFamilyAvatarDrawer(true);
        }}
      />
    ) : (
      <div />
    );
  };

  return (
    <OrbitDrawer
      drawerId={familyMemberDrawerId}
      heading={t('editFamilyDrawerTitle')}
      isOpen={openFamilyDetailsDrawer}
      handleClose={handleCloseFamilyDrawer}
      handleSubmit={handleSubmit}
      awaitSuccess={awaitSuccess}
      closeIcon="close"
      footer={true}
      saveButton={t('saveButtonLabel')}
      saveButtonDisabled={saveDisabled}
      dialogStatic={true}
      dialogRef={setDialogRef}
    >
      <InternalPage testId="edit-family-details">
        <Typography variant="body1" textAlign="left">
          {t('addDrawerFamilyDescription')}
        </Typography>
        <FirstDrawerDividerMargin orientation="horizontal" />
        <h2 className="headline-3 text-center mt-3">
          {t('displayPictureTitle')}
        </h2>
        <div className="my-4_5">
          {!familyDisplayPictureSaved && chosenFamilyDisplay !== 'picture' && (
            <FamilyDrawerDisplayPicture
              saveAvatarName={saveAvatarName}
              saveAvatarSkinTone={saveAvatarSkinTone}
              saveAvatarHairColor={saveAvatarHairColor}
              saveAvatarShirtColor={saveAvatarShirtColor}
            />
          )}
          <ProfileCropper
            isNewDependent={false}
            dependentId={dependentId}
            enrollmentContextType={enrollmentContextType}
            familyDisplayPictureSaved={familyDisplayPictureSaved}
            handleSaveFamilyPictureDrawer={handleSaveFamilyPictureDrawer}
          ></ProfileCropper>
        </div>
        <Box sx={{ textAlign: 'center' }}>{renderAvatarButton()}</Box>
      </InternalPage>
      <FirstDrawerDividerMargin orientation="horizontal" />
      <FamilyMemberForm
        ref={formRef}
        dependentId={dependentId}
        categories={categories}
        values={dependentInfo}
        handleChange={handleChange}
        formType="edit"
        onFormValidityChange={(isValid) => {
          setSaveDisabled(!isValid);
        }}
        isCropEnabled={chosenFamilyDisplay === 'picture' && !isFamilyCropped}
      />
      <AvatarFamilyDrawerDisplayPicture
        enrollmentContextType={enrollmentContextType}
        openFamilyAvatarDrawer={openFamilyAvatarDrawer}
        handleCloseAvatarDrawer={handleCloseAvatarDrawer}
        orientationDivider={orientationDivider}
        handleUploadDrawerAvatar={handleUploadDrawerAvatar}
        dependentId={dependentId}
      />
      <LoaderOverlay
        isAbsolute={true}
        isOverlay={true}
        isSubmitting={isSubmitting}
      />
    </OrbitDrawer>
  );
};
